import type { FunctionComponent } from "react";
import { useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import styled from "styled-components";

import { colorsPalettes, $robotoFontFamily } from "@similarweb/styles";
import { IconButton, Tab, TabList, Tabs, PopupClickContainerStateless } from "@similarweb/ui-components";

import { showSuccessToast } from "app/actions/toast_actions";
import categoryService from "app/common/services/categoryService";
import type { ICategory } from "app/common/services/categoryService.types";
import { isLocked } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { CustomCategoriesWizard } from "app/components/customCategoriesWizard/CustomCategoriesWizard";
import { ScrollContainer } from "app/components/layout/ScrollContainer";
import { useExceededLimitation } from "app/components/metering/UseLimitation";
import { SharingAssetModal } from "app/components/sharingAssetModal/SharingAssetModal";
import { ESharingState } from "app/components/sharingAssetModal/types";
import { HOOKS_ICON } from "app/constants/hooks";
import { usePagination } from "app/custom-hooks/usePagination";
import { i18nFilter } from "app/filters/i18nFilter";
import { DeleteIndustryModal } from "app/pages/industry-analysis/home/DeleteIndustryModal";
import { fetchEnrichedIndustries } from "app/pages/industry-analysis/home/enriched-custom-industries/fetchEnrichedIndustries";
import { IndustryItem } from "app/pages/industry-analysis/home/enriched-custom-industries/IndustryItem";
import type { IEnrichedIndustries } from "app/pages/industry-analysis/home/enriched-custom-industries/types";
import { ItemsHeader } from "app/pages/industry-analysis/home/ItemsHeader";
import { resolveCustomAttributes } from "app/pages/industry-analysis/home/resolveCustomAttributes";
import { EContext } from "app/pages/industry-analysis/home/types";
import { UserCustomCategoryService } from "app/services/category/userCustomCategoryService";
import { CategoryServiceUtils } from "app/services/CategoryServiceUtils";
import { openDefaultHooksModal } from "app/services/ModalService";
import { PreferencesService } from "app/services/preferences/preferencesService";
import { allTrackers } from "app/services/track/track";
import type { INumberRange } from "app/UtilitiesAndConstants/types/range";
import LocationService from "pro-features/components/Modals/src/UnlockModal/LocationService";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import TranslationProvider from "pro-features/components/WithTranslation/src/TranslationProvider";
import { IndustryAnalysisStartPage } from "pro-features/pages/module start page/src/industry analysis/IndustryAnalysisStartPage";
import { isIndustryAnalysisLimitationExceeded } from "pro-features/pages/module start page/src/industry analysis/utils/metering/IndustryAnalysisMetering";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { stateID } from "single-spa/react-app/router/stateID";

import NotificationModal from "./NotificationModal";
import { getModalText } from "./sharingModalCopy";
import { POPUP_CSS_CLASS_CONTAINER, PopupClickContainerStatelessWrapper } from "./StyledComponents";

const HIDE_MARKET_BUILDER_TOOLTIP_PREFERENCES_KEY = "hideMarketBuilderTooltipPreferences";

const PlaceholderText = styled.span`
    color: ${colorsPalettes.carbon["300"]};
    font-family: ${$robotoFontFamily};
    font-size: 14px;
    margin-left: 32px;
`;

const BoldPlaceholderText = styled.span`
    font-weight: 500;
`;

const TabsContainer = styled.span`
    > div {
        display: inline-block;
    }
`;

const PaginationContainer = styled.div`
    display: flex;
    justify-content: center;
    padding: 20px;
`;

const DEFAULT_PAGE_STATE = "digitalsuite_marketresearch_webmarketanalysis_mapping";

interface IIndustryAnalysisStartPageContainerProps {
    pageState?: string;
    subtitlePosition?: string;
    title: string;
    subtitle: string;
    marketBuilderState?: stateID;
}

enum ETabs {
    customCategories,
    sharedCustomCategories,
}

export const IndustryAnalysisStartPageContainer: FunctionComponent<IIndustryAnalysisStartPageContainerProps> = ({
    pageState = DEFAULT_PAGE_STATE,
    subtitlePosition = "left-aligned",
    title,
    subtitle,
    marketBuilderState = "market_research_builder",
}) => {
    const [showCustomCategoriesWizard, setShowCustomCategoriesWizard] = useState(false);
    const [isCreateMode, setIsCreateMode] = useState(false); // this can be deleted after market-builder is replacing the create wizard.
    const [selectedTab, setSelectedTab] = useState<ETabs>(ETabs.customCategories);
    const [customCategoriesEditItem, setCustomCategoriesEditItem] = useState(null);
    const [deleteConfirmModal, setDeleteConfirmModal] = useState({ isOpen: false, industry: null });
    const [isSharingModalOPen, setIsSharingModalOPen] = useState<boolean>(false);
    const [hideSegmentPopup, setHideSegmentPopup] = useState<boolean>(() => {
        const shouldHide = PreferencesService.get(HIDE_MARKET_BUILDER_TOOLTIP_PREFERENCES_KEY);
        return Boolean(shouldHide) || !categoryService.hasAdvancedCustomIndustriesPermission();
    });

    const dispatch = useDispatch();
    const sharedCustomCategories = UserCustomCategoryService.getSharedCustomCategories();
    const services = useMemo(() => {
        return {
            swNavigator: proNavigator,
            categoryService,
            swSettings,
            i18n: i18nFilter(),
        };
    }, []);
    const { i18n } = services;
    const [customCategories, setCustomCategories] = useState(UserCustomCategoryService.getCustomCategories());
    const [selectedIndustry, setSelectedIndustry] = useState<ICategory>();

    const getOnShareSubmit = (industry) => (sharing) => {
        const { sharingState, sharedWithUsers, notification } = sharing;
        const result = UserCustomCategoryService.updateSharingState(
            industry,
            sharingState,
            sharingState === ESharingState.ACCOUNT ? [swSettings.user.accountId] : [],
            sharingState === ESharingState.SUBSET_OF_USERS ? sharedWithUsers : [],

            notification,
        );
        result.then(() => {
            dispatch(showSuccessToast(i18n("market.research.share.industry.success.toast")));
            refreshIndustries();
        });

        return result;
    };

    useEffect(() => {
        // if the segmentPopup (tooltip) is not on hide mode then we want a tracking message to be triggered
        if (!hideSegmentPopup) {
            allTrackers.trackEvent("Pop Up", "shows", `Segment in custom industry`);
        }
    }, [hideSegmentPopup]);

    const isLimitationExceeded = useExceededLimitation(isIndustryAnalysisLimitationExceeded);
    const isCreatingCustomIndustryHooked =
        isLimitationExceeded || isLocked(swSettings.components.IndustryAnalysisMarketBuilder);

    const handleCreateNewCategoryClick = () => {
        if (isCreatingCustomIndustryHooked) {
            openDefaultHooksModal();
            return;
        }

        if (services.swSettings.components.Home.resources.IsCCAllowed) {
            if (categoryService.hasAdvancedCustomIndustriesPermission()) {
                services.swNavigator.go(marketBuilderState, {
                    returnToState: pageState,
                });
                allTrackers.trackEvent("Button", "click", `CustomIndustry/createCustomIndustry`);
            } else {
                setIsCreateMode(true);
                setShowCustomCategoriesWizard(true);
                allTrackers.trackEvent("Button", "click", `CustomIndustry/createCustomIndustry`);
                setCustomCategoriesEditItem(null);
            }
        } else {
            const location = `${LocationService.getCurrentLocationTracking()}/New Category`;
            openDefaultHooksModal("IndustryCustomCategories", location);
        }
    };

    const handleEditClick = (item) => () => {
        setIsCreateMode(false);
        setShowCustomCategoriesWizard(true);
        setCustomCategoriesEditItem(item);
    };

    const deleteClickHof = (industry: ICategory) => () => {
        setDeleteConfirmModal({ isOpen: true, industry });
    };
    const displaySuccessToast = () => {
        dispatch(showSuccessToast(i18n("market.research.copy.industry.success.toast")));
    };
    const refreshIndustries = () => {
        setCustomCategories(UserCustomCategoryService.getCustomCategories());
        loadEnrichedIndustries(pageItems);
    };
    const cloneIndustryHof = (industry: ICategory) => () => {
        UserCustomCategoryService.cloneSharedCustomIndustry(industry.categoryId)
            .then(refreshIndustries)
            .then(displaySuccessToast);
    };

    const handleItemClick =
        (item, context: EContext = EContext.LIST_ITEMS, query?: string) =>
        () => {
            const customAttributes = {
                ...resolveCustomAttributes(item),
                context: EContext[context],
                query,
            };
            allTrackers.dispatchTrackEvent("Button", "click", "Select Industry", customAttributes);
            const state = services.swNavigator.getState(pageState);
            const configId = services.swNavigator.getConfigId(state);
            const defaultParams = swSettings.components[configId].defaultParams;
            delete defaultParams.pageUrl;
            const ignoreFilterConsistency = Boolean(item.country);
            services.swNavigator.go(pageState, {
                ...defaultParams,
                ignoreFilterConsistency,
                country: CategoryServiceUtils.getIndustryCountry(item).id,
                category: item.forUrl,
                webSource: "Total",
                comparedDuration: "",
            });
        };
    const isSharedCustomCategories = selectedTab === ETabs.sharedCustomCategories;
    const getSortedCategories = () => {
        const customCategories = UserCustomCategoryService.getCustomCategories();
        const sharedCustomCategories = UserCustomCategoryService.getSharedCustomCategories();
        const categories = isSharedCustomCategories ? sharedCustomCategories : customCategories;
        const sortedCategories = categories.toSorted((category, otherCategory) => {
            const updatedTime = dayjs.utc(category.lastUpdated);
            const otherUpdatedTime = dayjs.utc(otherCategory.lastUpdated);
            return updatedTime.isBefore(otherUpdatedTime) ? 1 : -1;
        });
        return sortedCategories;
    };
    const sortedCategories = useMemo(() => getSortedCategories(), [selectedTab]);

    const [isLoading, setIsLoading] = useState(true);
    const itemsPerPage = 5;
    const [enrichedIndustries, setEnrichedIndustries] = useState<IEnrichedIndustries>(
        sortedCategories.slice(0, itemsPerPage),
    );
    const loadEnrichedIndustries = (pageItems: INumberRange) => {
        const { from, to } = pageItems;
        setIsLoading(true);
        const sortedCategories = getSortedCategories();
        const toBeNamed = sortedCategories.slice(from, to);
        setEnrichedIndustries(toBeNamed);
        fetchEnrichedIndustries(toBeNamed)
            .then(setEnrichedIndustries)
            .catch((e) => {})
            .finally(() => setIsLoading(false));
    };
    const callbackOnPageChange = (page: number, pageItems: INumberRange) => {
        loadEnrichedIndustries(pageItems);
    };

    const [pageItems, paginationComponent] = usePagination({
        listItemsLength: sortedCategories.length,
        itemsPerPage,
        callbackOnPageChange,
    });

    useEffect(() => {
        loadEnrichedIndustries(pageItems);
    }, [customCategories.length, selectedTab]);

    const getListItems = () => {
        const listItems = enrichedIndustries.map((customCategory, idx) => {
            const props = {
                industry: customCategory,
                isLoading,
                onClick: handleItemClick(customCategory),
                actionItemsProps: isSharedCustomCategories
                    ? {
                          onClone: cloneIndustryHof(customCategory),
                      }
                    : {
                          onEdit: handleEditClick(customCategory),
                          onShare: () => {
                              setSelectedIndustry(customCategory);
                              setIsSharingModalOPen(true);
                          },
                          onDelete: deleteClickHof(customCategory),
                      },
            };
            return (
                <IndustryItem
                    key={customCategory.categoryId}
                    {...props}
                />
            );
        });
        return [
            <div key={sortedCategories?.[0]?.categoryId}>
                <ItemsHeader />
                <FlexColumn gap={"12px"}>{listItems}</FlexColumn>
                {sortedCategories.length > itemsPerPage ? (
                    <PaginationContainer>{paginationComponent}</PaginationContainer>
                ) : null}
            </div>,
        ];
    };

    const PlaceholderComponent = () => (
        <PlaceholderText>
            {services.i18n("marketintelligence.marketresearch.webmarketanalysis.home.searchText")}{" "}
            <BoldPlaceholderText>
                {services.i18n("marketintelligence.marketresearch.webmarketanalysis.home.searchItem")}
            </BoldPlaceholderText>
        </PlaceholderText>
    );

    const disableSegmentPopup = () => {
        PreferencesService.add({ [HIDE_MARKET_BUILDER_TOOLTIP_PREFERENCES_KEY]: true });
        setHideSegmentPopup(true);
    };

    const onCloseSegmentPopupBtnClick = () => {
        disableSegmentPopup();
        allTrackers.trackEvent("Pop Up", "click", `Segment in custom industry/Dismiss`);
    };

    const onSegmentPopupBtnClick = () => {
        disableSegmentPopup();
        allTrackers.trackEvent("Pop Up", "click", `Segment in custom industry/Try it now`);
        handleCreateNewCategoryClick();
    };

    const notificationModalContent = () => (
        <NotificationModal
            title={i18n("industry.analysis.home.popup.segment.title")}
            subtitle={i18n("industry.analysis.home.popup.segment.subtitle")}
            btnText={i18n("industry.analysis.home.popup.segment.btntext")}
            onBtnClick={onSegmentPopupBtnClick}
            onClose={onCloseSegmentPopupBtnClick}
        />
    );

    const ctaButton = [
        <PopupClickContainerStatelessWrapper
            key="create-custom-industry-btn"
            className="create-custom-industry-btn"
        >
            <PopupClickContainerStateless
                content={notificationModalContent}
                appendTo={".create-custom-industry-btn"}
                config={{
                    placement: "bottom-right",
                    width: 265,
                    defaultOpen: true,
                    cssClassContainer: POPUP_CSS_CLASS_CONTAINER,
                    enabled: true,
                }}
                isOpen={!hideSegmentPopup ? !showCustomCategoriesWizard : null}
            >
                <div>
                    <IconButton
                        iconName={isCreatingCustomIndustryHooked ? HOOKS_ICON : "add"}
                        type={"primary"}
                        onClick={handleCreateNewCategoryClick}
                    >
                        <span
                            style={{ display: "flex", alignItems: "center" }}
                            data-automation="create-custom-industry"
                        >
                            <I18n>industry.research.market.builder.create</I18n>
                        </span>
                    </IconButton>
                </div>
            </PopupClickContainerStateless>
        </PopupClickContainerStatelessWrapper>,
    ];

    const onSelectedTabChange = (index) => {
        const tab = index === ETabs.customCategories ? "My Industries" : "Shared Custom Industries";
        allTrackers.trackEvent("Tab", "click", `Table/${tab}`);
        setSelectedTab(index);
    };

    const headerTabs = (
        <TabsContainer>
            <Tabs
                selectedIndex={selectedTab}
                onSelect={onSelectedTabChange}
            >
                <TabList>
                    <Tab>
                        {i18n("industry.analysis.home.tabs.market", {
                            count: customCategories.length,
                        })}
                    </Tab>
                    <Tab>
                        {i18n("industry.analysis.home.tabs.markets.shared", {
                            count: sharedCustomCategories.length,
                        })}
                    </Tab>
                </TabList>
            </Tabs>
        </TabsContainer>
    );

    const homepageProps = {
        subtitlePosition,
        title,
        subtitle,
        bodyTextComponent: headerTabs,
    };

    const disabledSharingStates = useMemo(() => {
        if (!selectedIndustry) {
            return [];
        }
        const { categoryId, name } = selectedIndustry;
        const disabledSharingStates = CategoryServiceUtils.resolveDisabledSharingStates(selectedIndustry);
        if (disabledSharingStates.length > 0) {
            allTrackers.trackEvent(
                "Disable state",
                "Share custom industry",
                `Sharing options disabled/${name}/${categoryId}`,
            );
        }
        return disabledSharingStates;
    }, [selectedIndustry]);
    return (
        <TranslationProvider translate={services.i18n}>
            <ScrollContainer>
                <CustomCategoriesWizard
                    isOpen={showCustomCategoriesWizard}
                    onClose={() => setShowCustomCategoriesWizard(false)}
                    wizardProps={{
                        onSave: async () => {
                            await setCustomCategories(UserCustomCategoryService.getCustomCategories());
                            if (customCategoriesEditItem) {
                                setShowCustomCategoriesWizard(false);
                            }
                            loadEnrichedIndustries(pageItems);
                        },
                        customCategoryName: customCategoriesEditItem?.text ?? null,
                        stayOnPage: !!customCategoriesEditItem,
                        isCreateMode: isCreateMode,
                    }}
                />
                <DeleteIndustryModal
                    isOpen={deleteConfirmModal.isOpen}
                    industry={deleteConfirmModal.industry}
                    setIsOpen={(isOpen) => setDeleteConfirmModal({ ...deleteConfirmModal, isOpen })}
                    onDelete={refreshIndustries}
                />
                {selectedIndustry ? (
                    <SharingAssetModal
                        onSubmit={getOnShareSubmit(selectedIndustry)}
                        modalText={getModalText(selectedIndustry)}
                        sharedWithUsers={selectedIndustry.sharedWithUsers}
                        initialSharingState={selectedIndustry.sharingState}
                        isOpen={isSharingModalOPen}
                        setIsOpen={setIsSharingModalOPen}
                        disabledSharingStates={disabledSharingStates}
                        customTrackingAttributesOnShare={resolveCustomAttributes(selectedIndustry)}
                    />
                ) : null}
                <IndustryAnalysisStartPage
                    listButtons={ctaButton}
                    listItems={getListItems()}
                    handleItemClick={handleItemClick}
                    autocompletePlaceholder={PlaceholderComponent()}
                    {...homepageProps}
                />
            </ScrollContainer>
        </TranslationProvider>
    );
};
